import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Scale,
  LayoutDashboard,
  Users,
  Search,
  Radar,
  Gavel,
  Activity,
  ShieldAlert,
  Calculator,
  FileText,
  CreditCard,
  Settings,
  LogOut,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import JeaBadge from "@/components/jea/JeaBadge";
import NotificationBell from "@/components/jea/NotificationBell";
import HelpModal from "@/components/jea/HelpModal";
import PaywallModal from "@/components/jea/PaywallModal";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/lib/apiClient";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/debtors", label: "My Judgments", icon: Users },
  { to: "/discovery", label: "Discovery", icon: Radar },
  { to: "/enforcement", label: "Enforcement", icon: Gavel },
  { to: "/monitoring", label: "Monitoring", icon: Activity },
  { to: "/bankruptcy", label: "Bankruptcy Watch", icon: ShieldAlert, badge: "bankruptcy" },
  { to: "/interest", label: "Interest Calculator", icon: Calculator },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/run-query", label: "Run Query", icon: Search, capability: "canRunQueries" },
  { to: "/billing", label: "Billing", icon: CreditCard, badge: "billing", capability: "canManageBilling" },
  { to: "/settings", label: "Settings", icon: Settings },
];

const SidebarBadge = ({ tone, children, testid }) => (
  <span
    className={cn(
      "ml-auto inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold tracking-[0.02em] animate-pulse-alert",
      tone === "danger" && "bg-danger text-white",
      tone === "warning" && "bg-warning text-[#091424]",
    )}
    data-testid={testid}
  >
    {children}
  </span>
);

const initialsOf = (name = "") => {
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] || "?";
  const b = parts[parts.length - 1]?.[0] || "";
  return (a + (parts.length > 1 ? b : "")).toUpperCase();
};

const pageTitleFor = (pathname) => {
  if (pathname.startsWith("/debtors")) return "My Judgments";
  if (pathname.startsWith("/interest")) return "Interest Calculator";
  if (pathname.startsWith("/enforcement")) return "Enforcement";
  if (pathname.startsWith("/discovery")) return "Discovery";
  const item = NAV.find((n) => pathname === n.to || pathname.startsWith(n.to + "/"));
  return item?.label || "Judgment Enforcement AI";
};

const SidebarSlotMeter = ({ used, max, onUpgrade }) => {
  const pct = max ? Math.min(100, (used / max) * 100) : 0;
  const full = max && used >= max;
  return (
    <div className="mt-3 space-y-1" data-testid="sidebar-slot-meter">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-text-secondary">Judgment slots</span>
        <span className="text-white font-semibold">{used}/{max}</span>
      </div>
      <div className={cn("h-1 w-full rounded bg-border-default overflow-hidden", full && "shadow-pink-glow")}>
        <motion.div
          className="h-full bg-accent-pink"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>
      {full && (
        <button
          type="button"
          onClick={onUpgrade}
          className="mt-1 w-full text-[11px] font-semibold text-accent-pink hover:text-accent-pink-light border border-accent-pink rounded-md h-7 transition-colors"
          data-testid="sidebar-slot-upgrade"
        >
          Upgrade to PRO
        </button>
      )}
    </div>
  );
};

const AuthShell = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const perms = useRolePermissions();
  const title = pageTitleFor(location.pathname);
  const [slot, setSlot] = useState({ used: 0, max: null });
  const [bankruptcyBadge, setBankruptcyBadge] = useState(0);
  const [billingBadge, setBillingBadge] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [paywall, setPaywall] = useState({ open: false, feature: "this feature", message: "" });

  const inPreview = !!user && user.onboarding_deferred && !user.onboarding_completed;

  // Filter sidebar nav by role capabilities.
  const visibleNav = React.useMemo(
    () => NAV.filter((item) => !item.capability || perms[item.capability]),
    [perms]
  );

  // Direct-URL block: if the current pathname is hidden for this role,
  // redirect + toast. Backend still enforces 403 on the API layer.
  React.useEffect(() => {
    if (!user) return;
    if (perms.isRouteBlocked(location.pathname)) {
      toast.error("You don't have permission to view this page.");
      nav("/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname, perms.isRouteBlocked]);

  useEffect(() => {
    const handler = (e) => {
      setPaywall({
        open: true,
        feature: e?.detail?.feature || "this feature",
        message: e?.detail?.message || "",
      });
    };
    window.addEventListener("jea:paywall", handler);
    return () => window.removeEventListener("jea:paywall", handler);
  }, []);

  const refreshSlot = async () => {
    if (user?.account_type !== "standard") return;
    try {
      const { data } = await apiClient.get("/debtors");
      setSlot({ used: data.slot_used || 0, max: data.slot_max ?? null });
    } catch {
      /* ignore */
    }
  };

  const refreshSidebarBadges = async () => {
    try {
      const { data } = await apiClient.get("/bankruptcy/reviews/count");
      setBankruptcyBadge(data.unreviewed_count || 0);
    } catch { /* ignore */ }
    if (!perms.canManageBilling) {
      setBillingBadge(null);
      return;
    }
    try {
      const { data } = await apiClient.get("/billing/summary");
      const s = data?.subscription?.status;
      setBillingBadge(s === "canceling" || s === "past_due" ? s : null);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (user?.account_type === "standard") refreshSlot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.pathname]);

  // Poll sidebar badges every 30 s while authenticated
  useEffect(() => {
    if (!user?.id) return;
    refreshSidebarBadges();
    const t = setInterval(refreshSidebarBadges, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out.");
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-bg-primary text-white flex" data-testid="auth-shell">
      {/* Sidebar */}
      <aside
        className="w-[240px] shrink-0 h-screen sticky top-0 bg-bg-secondary border-r border-border-default flex flex-col"
        data-testid="sidebar"
      >
        <div className="px-5 pt-6 pb-4">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Scale className="text-accent-pink" size={20} strokeWidth={2.5} />
            <span className="font-extrabold text-[16px] text-accent-pink tracking-tight leading-tight">
              Judgment
              <br />
              Enforcement AI
            </span>
          </Link>
        </div>
        <div className="mx-5 h-px bg-accent-pink/20 mb-2" />

        <nav className="flex-1 overflow-y-auto py-2" data-testid="sidebar-nav">
          {visibleNav.map(({ to, label, icon: Icon, badge }) => {
            const showBankruptcyBadge = badge === "bankruptcy" && bankruptcyBadge > 0;
            const showBillingBadge    = badge === "billing" && billingBadge;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${to.replace("/", "")}`}
                className={({ isActive }) =>
                  cn(
                    "relative flex items-center gap-3 h-12 pl-4 pr-3 text-[14px] font-medium transition-all duration-150",
                    isActive
                      ? "bg-bg-tertiary text-accent-pink"
                      : "text-text-secondary hover:bg-bg-tertiary hover:text-white"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-accent-pink" />
                    )}
                    <Icon size={18} className={isActive ? "text-accent-pink" : ""} />
                    <span className="truncate">{label}</span>
                    {showBankruptcyBadge && (
                      <SidebarBadge tone="danger" testid="sidebar-badge-bankruptcy">
                        {bankruptcyBadge > 9 ? "9+" : bankruptcyBadge}
                      </SidebarBadge>
                    )}
                    {showBillingBadge && (
                      <SidebarBadge tone="warning" testid="sidebar-badge-billing">
                        !
                      </SidebarBadge>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-border-default p-4">
          <div className="flex items-center gap-3" data-testid="sidebar-user">
            <div className="w-9 h-9 rounded-full bg-accent-pink text-[#091424] font-bold text-[14px] flex items-center justify-center" aria-hidden>
              {initialsOf(user?.full_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[14px] font-semibold truncate" data-testid="sidebar-username">
                {user?.full_name || "—"}
              </div>
              <div className="text-[11px] tracking-[0.08em] text-text-secondary uppercase flex items-center gap-1.5" data-testid="sidebar-account-type">
                {perms.isTeamMember ? (
                  <span className="text-accent-pink" data-testid="sidebar-role-badge">
                    {(perms.role || "").replace("_", " ").toUpperCase()}
                  </span>
                ) : (
                  <span>{(user?.account_type || "standard").toUpperCase()}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="p-2 rounded-md text-text-secondary hover:text-accent-pink hover:bg-bg-tertiary transition-colors"
              aria-label="Sign out"
              data-testid="sidebar-logout"
            >
              <LogOut size={16} />
            </button>
          </div>
          {user?.account_type === "standard" && slot.max != null && (
            <SidebarSlotMeter
              used={slot.used}
              max={slot.max}
              onUpgrade={() => nav("/billing?open=change-plan&target=pro-monthly")}
            />
          )}
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top header */}
        <header
          className="h-16 bg-bg-primary border-b border-border-default flex items-center justify-between px-8"
          data-testid="top-header"
        >
          <h1 className="text-[20px] font-bold text-white" data-testid="top-header-title">
            {title}
          </h1>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="p-2 rounded-full text-text-secondary hover:text-accent-pink hover:bg-bg-secondary transition-colors"
              title="Help"
              aria-label="Help"
              data-testid="header-help-btn"
            >
              <span className="inline-flex items-center justify-center w-5 h-5">?</span>
            </button>
          </div>
        </header>

        {/* Preview-mode nag banner */}
        {inPreview && (
          <div
            className="w-full h-[52px] flex items-center justify-between px-8 border-l-[4px]"
            style={{
              background: "linear-gradient(90deg, rgba(245,184,0,0.10) 0%, rgba(245,184,0,0.05) 100%)",
              borderLeftColor: "var(--color-tier-gold)",
            }}
            data-testid="preview-mode-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles size={18} style={{ color: "var(--color-tier-gold)" }} />
              <span className="text-white text-[14px] font-medium truncate">
                You're in preview mode — some features are locked. Complete onboarding to unlock everything.
              </span>
            </div>
            <button
              type="button"
              onClick={() => nav("/onboarding")}
              style={{ background: "var(--color-tier-gold)", color: "#091424" }}
              className="h-9 px-4 rounded-[8px] font-bold text-[13px] hover:opacity-90 transition-opacity"
              data-testid="preview-mode-resume-btn"
            >
              Resume Onboarding
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-[1320px] mx-auto px-8 py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              >
                <Outlet context={{ inPreview }} />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <PaywallModal
        open={paywall.open}
        feature={paywall.feature}
        message={paywall.message}
        onClose={() => setPaywall({ open: false, feature: "this feature", message: "" })}
      />
    </div>
  );
};

export const AccountBadge = ({ type }) => {
  const variant = type === "pro" ? "pink-tier" : "silver";
  return <JeaBadge variant={variant}>{type?.toUpperCase() || "STANDARD"}</JeaBadge>;
};

export default AuthShell;
